import getDeepKeyPath from '@utils/form/getDeepKeyPath'
import toISOStringWithoutUTC from '@utils/date/toISOStringWithoutUTC'

export type TCasterResult = string
export type TCasterPayload = string
export type TCasterCallback = (value: TCasterPayload) => TCasterResult
export type TCasterQuery = TCasterCallback[]
export type TCasterMethods = keyof Omit<Caster, 'query' | 'execute'>

export type TCasterCastData<TFieldValues = Record<string, any>> = {
  [key in keyof TFieldValues]: any
}
export type TCasterCastRules<TFieldValues = Record<string, any>> = {
  [key in keyof TFieldValues]?: Caster
}

class Caster {
  private __field__ = ''
  private __input__: TCasterPayload = ''
  private __result__: TCasterResult = ''
  private __query__: TCasterQuery = []

  private castFn(callback: TCasterCallback, args?: any[]): Caster {
    this.__query__.push(callback.bind(args))
    return this
  }

  get query(): TCasterQuery {
    return this.__query__
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  set query(_) {
    console.error(
      `You shouldn't change Caster.query directly.
      Use Caster functions like .phone(), .trimWhitespace(), etc... to set query`,
    )
  }

  execute(value: TCasterPayload): TCasterResult {
    this.__input__ = value
    this.__result__ = value
    this.__query__.forEach((cb) => {
      this.__result__ = cb(this.__result__)
    })
    return this.__result__
  }

  setFieldName(name: string) {
    this.__field__ = name
    return this
  }

  /* ========= SIMPLE ========= */
  custom(callback: TCasterCallback) {
    return this.castFn(callback)
  }

  /**
   * Обрезает пробелы
   */
  noWhiteSpace() {
    return this.castFn((value) => {
      if (!value) return ''
      return value.replace(/\s/g, '')
    })
  }

  /**
   * Убирает спецсимволы
   */
  noSpecialChars() {
    return this.castFn((value) => {
      if (!value) return ''
      return value.replace(/[^a-zA-Z0-9А-Яа-яёЁ ]/g, '')
    })
  }

  /**
   * Оставляет только цифры
   */
  digitsOnly() {
    return this.castFn((value) => {
      if (!value) return ''
      return value.replace(/[^0-9]/g, '')
    })
  }

  /**
   * Оставляет только русские буквы и пробелы
   */
  rusOnly() {
    return this.castFn((value) => {
      if (!value) return value
      return value.replace(/[^А-Яа-яёЁ ]/g, '')
    })
  }

  /**
   * Оставляет только английские буквы и пробелы
   */
  engOnly() {
    return this.castFn((value) => {
      if (!value) return value
      return value.replace(/[^A-Za-z ]/g, '')
    })
  }

  digitsWithEng() {
    return this.castFn((value) => {
      return value?.replace(/[^0-9A-Za-z]/g, '')
    })
  }

  /* ========= COMBINED ========= */
  /**
   * Форматирует номер телефона
   */
  phone() {
    return this.noSpecialChars()
      .digitsOnly()
      .noWhiteSpace()
      .castFn((value) => {
        if (!value) return ''
        return value.substring(value.length, value.length - 10)
      })
  }

  /**
   * Оставляет только цифры и символы
   * */
  numbersWithLetters() {
    return this.noSpecialChars().digitsWithEng()
  }

  passport() {
    return this.noSpecialChars().digitsOnly().noWhiteSpace()
  }

  dateUTC() {
    return this.castFn((value: Date | string | null) => {
      if (!value) return ''
      if (value instanceof Date) return value.toUTCString()
      return new Date(value).toUTCString()
    })
  }

  dateISO() {
    return this.castFn((value: Date | string | null) => {
      if (!value) return ''
      if (value instanceof Date) return value.toISOString()
      return new Date(value).toISOString()
    })
  }

  dateISOWithoutUTC() {
    return this.castFn((value: Date | string | null) => {
      if (!value) return ''
      if (value instanceof Date) return toISOStringWithoutUTC(value)
      return toISOStringWithoutUTC(new Date(value))
    })
  }

  static cast<TFieldValues>(data: TCasterCastData<TFieldValues>, rules: TCasterCastRules<TFieldValues>) {
    const castedData = { ...data }

    for (const fieldName in rules) {
      const parts = getDeepKeyPath(fieldName)

      let fieldObject = castedData as Record<string, any>
      let field = parts.shift() || ''
      for (const fieldNext of parts) {
        fieldObject = fieldObject[field]
        if (!fieldObject) break
        field = fieldNext
      }
      if (!fieldObject) continue
      const caster = rules[fieldName]?.setFieldName(fieldName)
      if (caster) {
        const newValue = caster.execute(fieldObject[field])
        if (newValue !== undefined) fieldObject[field] = newValue
      }
    }
    return castedData
  }
}

export default Caster
